import { useId, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';

/**
 * Rückmeldungen an den Benutzer: Hinweise, Statuschips und die kleine
 * Fragezeichen-Hilfe.
 *
 * Grundsatz aus 02_navigation_ux_tutorial_i18n.md: verständliche Sprache,
 * keine technischen Begriffe. Und: ein Zustand wird nie allein durch Farbe
 * vermittelt - der Text sagt dasselbe.
 */

export type Ton = 'info' | 'error' | 'success' | 'warning';

export function Alert({ tone, children }: { tone: Ton; children: ReactNode }): JSX.Element {
  return (
    <div className={`notice notice-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Bisheriger Name derselben Sache - bleibt, damit nichts unnötig umzieht. */
export function Notice({
  tone,
  children,
}: {
  tone: 'info' | 'error' | 'success';
  children: ReactNode;
}): JSX.Element {
  return <Alert tone={tone}>{children}</Alert>;
}

export type ChipTon = 'neutral' | 'erfolg' | 'info' | 'warnung' | 'gefahr';

/**
 * Statuschip.
 *
 * Der Text trägt die Bedeutung, die Farbe verstärkt sie nur. Wer keine Farben
 * unterscheiden kann, liest trotzdem dasselbe.
 */
export function StatusChip({ tone = 'neutral', children }: { tone?: ChipTon; children: ReactNode }): JSX.Element {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

/**
 * Kleines `?` mit kurzer Erklärung.
 *
 * Erreichbar per Maus (Hover) und per Tastatur (Fokus) - deshalb ein echter
 * Button und kein `title`-Attribut, das die Tastatur nicht erreicht.
 * Die Erklärung ist ein bis drei Sätze lang, keine Dokumentation.
 */
export function HelpIcon({ text, label }: { text: string; label?: string }): JSX.Element {
  const [offen, setOffen] = useState(false);
  const id = useId();
  const { t } = useI18n();

  return (
    <span
      className="hilfe"
      onMouseEnter={() => setOffen(true)}
      onMouseLeave={() => setOffen(false)}
    >
      <button
        type="button"
        className="hilfe-knopf"
        aria-label={label ? t('help.aboutLabel', { label }) : t('help.about')}
        aria-describedby={offen ? id : undefined}
        aria-expanded={offen}
        onFocus={() => setOffen(true)}
        onBlur={() => setOffen(false)}
        onClick={() => setOffen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOffen(false);
          }
        }}
      >
        ?
      </button>
      {offen ? (
        <span className="hilfe-text" id={id} role="tooltip">
          {text}
        </span>
      ) : null}
    </span>
  );
}
